import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApiError, adminApiMessage, evalsApi } from '../adminApi';
import { EVAL_AGENTS, agentLabel, agentVersions, runAgents } from '../evals/agents';
import {
  EvalBadge,
  EvalError,
  FigureCell,
  GateBadge,
  RunLink,
  StatusPill,
} from '../evals/EvalBits';
import {
  formatUsd,
  gradingLabel,
  judgeShortText,
  metricLabel,
  modelShortName,
  runListMetrics,
  shortSha,
} from '../evals/format';
import { MIXED_CODE_LABEL } from '../evals/progress';
import {
  EVAL_PAGE_SIZE,
  EVAL_RUN_STATUSES,
  pageOffsets,
  runFilterFromSearch,
  runFilterSearch,
  runHref,
} from '../evals/query';
import type { EvalMetric, EvalRunFilter, EvalRunList, EvalRunListItem } from '../evals/types';
import {
  importErrorText,
  importOutcomeText,
  readRunFile,
  type EvalImportOutcome,
} from '../evals/upload';
import { GuideLink } from '../evals/GuideLink';
import { formatDate } from '../format';

// Every stored eval run (task eval_admin_runs; ADR 0232), newest first, with
// the filters in the page URL. A run file `--out` wrote can be uploaded here:
// it goes through the import endpoint (task eval_run_store), which stores it
// once by content hash and audits it.

/** Short column heads; the cell title carries the metric's full name. */
const METRIC_HEADS: Record<EvalMetric, string> = {
  attacks: 'Attacks',
  helpful: 'Helpful',
  scopeGap: 'Scope gap',
  questions: 'Questions',
  conditions: 'Conditions',
  decisions: 'Decisions',
};

/**
 * One run: a table row per model, so each model's gate and figures read on
 * one line, with the run's own cells spanning them.
 */
function RunRows({ run, metrics }: { run: EvalRunListItem; metrics: readonly EvalMetric[] }) {
  const span = Math.max(1, run.models.length);
  const runCells = (
    <>
      <td rowSpan={span}>
        <span className="eval-stack">
          <RunLink id={run.id} />
          <span className="admin-muted eval-sub eval-nowrap">
            {formatDate(run.startedAt ?? run.createdAt)}
          </span>
          {/* Which agent the run tested, where a phone sees it first (TOM-533). */}
          {run.models.length > 0 ? (
            <span className="eval-sub eval-nowrap" data-testid="eval-run-agents">
              {runAgents(run.models).map(agentLabel).join(' and ')}
            </span>
          ) : null}
        </span>
      </td>
      <td rowSpan={span}>
        <span className="eval-stack">
          <span>
            {run.suite}
            {/* A long run's split, or an expert's over its own cases (TOM-533). */}
            {run.split !== null ? ` · ${run.split}` : ''}
          </span>
          <span className="admin-muted eval-sub eval-nowrap">
            {run.repeats}×{run.fullSelection ? '' : ' · part'}
            {run.runKind !== 'collect' ? ` · ${run.runKind}` : ''}
          </span>
        </span>
      </td>
      <td rowSpan={span}>
        <StatusPill status={run.status} stalled={run.stalled} />
      </td>
    </>
  );
  const tailCells = (
    <>
      <td rowSpan={span}>{formatUsd(run.totalCostUsd)}</td>
      <td rowSpan={span}>
        <span className="eval-stack eval-nowrap">
          <span>
            <code>{shortSha(run.gitSha)}</code>
            {run.gitDirty === true ? <span className="admin-muted"> dirty</span> : null}
            {run.mixedCode ? (
              <>
                {' '}
                <EvalBadge
                  tone="warn"
                  title="Its shards ran on different code (a deploy landed mid-run): its figures are not comparable with another run's"
                  testId="eval-mixed-code"
                >
                  {MIXED_CODE_LABEL}
                </EvalBadge>
              </>
            ) : null}
          </span>
          {/* Each agent's own version (TOM-533): an expert's run names the expert's. */}
          {agentVersions(run, run.models).map((a) => (
            <span
              key={a.role}
              className="admin-muted eval-sub"
              title={a.label}
              data-testid="eval-run-version"
            >
              {a.short} <code>{a.version ?? '—'}</code>
            </span>
          ))}
        </span>
      </td>
    </>
  );
  if (run.models.length === 0) {
    return (
      <tbody data-testid={`eval-run-row-${run.id}`} className="eval-run-group">
        <tr>
          {runCells}
          <td colSpan={3 + metrics.length} className="admin-muted">
            No model rows yet
          </td>
          {tailCells}
        </tr>
      </tbody>
    );
  }
  return (
    <tbody data-testid={`eval-run-row-${run.id}`} className="eval-run-group">
      {run.models.map((m, i) => (
        <tr key={`${m.model}|${m.role}`}>
          {i === 0 ? runCells : null}
          <td className="eval-nowrap" title={m.model}>
            <span className="eval-stack">
              <span>
                <code>{modelShortName(m.model)}</code>
                {m.baseline ? (
                  <>
                    {' '}
                    <EvalBadge tone="succeeded" title="The pinned baseline for this model">
                      Baseline
                    </EvalBadge>
                  </>
                ) : null}
              </span>
              {/* Which agent the row ran (TOM-533). */}
              <span className="admin-muted eval-sub" data-testid="eval-run-agent">
                {agentLabel(m.role)}
              </span>
            </span>
          </td>
          <td>
            <GateBadge gate={m.gate} reason={m.gateReason} />
          </td>
          {metrics.map((metric) => (
            <td key={metric} title={metricLabel(metric)}>
              <FigureCell figure={m.figures[metric]} repeats={run.repeats} />
            </td>
          ))}
          <td className="eval-nowrap" title={gradingLabel(m.grading)}>
            {judgeShortText(m)}
          </td>
          {i === 0 ? tailCells : null}
        </tr>
      ))}
    </tbody>
  );
}

function Filters({
  filter,
  onChange,
}: {
  filter: EvalRunFilter;
  onChange: (f: EvalRunFilter) => void;
}) {
  const [model, setModel] = useState(filter.model ?? '');
  const [promptVersion, setPromptVersion] = useState(filter.promptVersion ?? '');
  useEffect(() => setModel(filter.model ?? ''), [filter.model]);
  useEffect(() => setPromptVersion(filter.promptVersion ?? ''), [filter.promptVersion]);

  function submitText(e: FormEvent) {
    e.preventDefault();
    const m = model.trim();
    const p = promptVersion.trim();
    onChange({
      ...filter,
      model: m.length > 0 ? m : undefined,
      promptVersion: p.length > 0 ? p : undefined,
      offset: undefined,
    });
  }

  function set<K extends keyof EvalRunFilter>(key: K, value: EvalRunFilter[K] | undefined) {
    onChange({ ...filter, [key]: value, offset: undefined });
  }

  return (
    <form className="eval-filters" onSubmit={submitText} aria-label="Filter runs">
      <label>
        Suite
        <select
          value={filter.suite ?? ''}
          onChange={(e) => set('suite', (e.target.value || undefined) as EvalRunFilter['suite'])}
        >
          <option value="">Any</option>
          <option value="short">Short</option>
          <option value="long">Long</option>
        </select>
      </label>
      <label>
        Agent
        {/* Every agent the eval runs (TOM-533): the API filters on its role. */}
        <select
          value={filter.role ?? ''}
          data-testid="eval-runs-agent"
          onChange={(e) => set('role', e.target.value || undefined)}
        >
          <option value="">All</option>
          {EVAL_AGENTS.map((a) => (
            <option key={a.role} value={a.role}>
              {a.label}
            </option>
          ))}
          {filter.role !== undefined && !EVAL_AGENTS.some((a) => a.role === filter.role) ? (
            <option value={filter.role}>{agentLabel(filter.role)}</option>
          ) : null}
        </select>
      </label>
      <label>
        Status
        <select
          value={filter.status ?? ''}
          onChange={(e) => set('status', (e.target.value || undefined) as EvalRunFilter['status'])}
        >
          <option value="">Any</option>
          {EVAL_RUN_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>
      </label>
      <label>
        Launched by
        <select
          value={filter.launchedBy ?? ''}
          onChange={(e) =>
            set('launchedBy', (e.target.value || undefined) as EvalRunFilter['launchedBy'])
          }
        >
          <option value="">Anyone</option>
          <option value="cli">CLI or import</option>
          <option value="admin">Admin console</option>
          <option value="schedule">Schedule</option>
        </select>
      </label>
      <label>
        Kind
        <select
          value={filter.runKind ?? ''}
          onChange={(e) =>
            set('runKind', (e.target.value || undefined) as EvalRunFilter['runKind'])
          }
        >
          <option value="">Any</option>
          <option value="collect">Collect</option>
          <option value="regrade">Re-grade</option>
          <option value="calibration">Calibration</option>
        </select>
      </label>
      <label>
        Baseline
        <select
          value={filter.baseline === undefined ? '' : String(filter.baseline)}
          onChange={(e) =>
            set('baseline', e.target.value === '' ? undefined : e.target.value === 'true')
          }
        >
          <option value="">Any</option>
          <option value="true">Pinned</option>
          <option value="false">Not pinned</option>
        </select>
      </label>
      <label>
        Model
        <input
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="e.g. opus or openrouter:…"
          maxLength={200}
        />
      </label>
      <label>
        Prompt version
        <input
          type="text"
          value={promptVersion}
          onChange={(e) => setPromptVersion(e.target.value)}
          placeholder="e.g. v61, v61+3f9a2c1d or el1"
          maxLength={40}
        />
      </label>
      <button type="submit" className="admin-button-secondary">
        Apply
      </button>
    </form>
  );
}

function Upload({ onStored }: { onStored: (o: EvalImportOutcome) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<
    { kind: 'ok'; outcome: EvalImportOutcome } | { kind: 'error'; message: string } | null
  >(null);

  async function onFile(file: File | undefined) {
    if (file === undefined) return;
    setBusy(true);
    setResult(null);
    try {
      const read = await readRunFile(file);
      if (!read.ok) {
        setResult({ kind: 'error', message: read.message });
        return;
      }
      const outcome = await evalsApi.importRunFile(read.upload);
      setResult({ kind: 'ok', outcome });
      onStored(outcome);
    } catch (err) {
      const body = adminApiError(err);
      setResult({ kind: 'error', message: importErrorText(body.error, body.message) });
    } finally {
      setBusy(false);
      if (input.current !== null) input.current.value = '';
    }
  }

  return (
    <div className="eval-upload">
      <label className="admin-button-secondary eval-upload-button" aria-busy={busy}>
        {busy ? 'Uploading…' : 'Upload a run file'}
        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          data-testid="eval-upload-input"
          disabled={busy}
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
      </label>
      <span className="admin-muted eval-sub">
        The JSON file <code>pnpm neo:eval --out</code> writes.
      </span>
      {result !== null ? (
        <div
          className={result.kind === 'ok' ? 'eval-upload-result' : 'admin-auth-banner error'}
          data-testid="eval-upload-result"
          role="status"
        >
          {result.kind === 'ok' ? (
            <>
              {importOutcomeText(result.outcome)}{' '}
              <Link to={runHref(result.outcome.runId)} data-testid="eval-upload-run-link">
                Open run <code>{result.outcome.runId.slice(0, 8)}</code> →
              </Link>
            </>
          ) : (
            result.message
          )}
        </div>
      ) : null}
    </div>
  );
}

export function AdminEvalRuns() {
  const [search, setSearch] = useSearchParams();
  const filter = runFilterFromSearch(search);
  const filterKey = runFilterSearch(filter);
  const [data, setData] = useState<EvalRunList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let ignore = false;
    setError(null);
    evalsApi
      .runs(runFilterFromSearch(new URLSearchParams(filterKey)))
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (!ignore) setError(adminApiMessage(err, 'Failed to load eval runs.'));
      });
    return () => {
      ignore = true;
    };
  }, [filterKey, reload]);

  function apply(next: EvalRunFilter) {
    setSearch(new URLSearchParams(runFilterSearch(next)));
  }

  const pages = data !== null ? pageOffsets(data.total, data.offset, data.limit) : null;
  const metrics = runListMetrics(data?.runs ?? []);

  return (
    <section className="admin-section eval-page">
      <header className="admin-section-header">
        <h1>Eval runs</h1>
        <span className="admin-count">
          {data !== null ? `${data.total} ${data.total === 1 ? 'run' : 'runs'}` : null}
        </span>
        <GuideLink section="run" />
      </header>
      <p className="admin-hint">
        Every stored run, newest first. Rates read each case&apos;s majority across its repeats; the
        interval under a rate is its 95% interval when the run had repeats.
      </p>

      <p className="eval-actions">
        {/* TOM-120: launch a run on the worker, inside a budget. */}
        <Link
          className="admin-button-affirm"
          to="/admin/evals/launch"
          data-testid="eval-launch-link"
        >
          Launch a run
        </Link>
      </p>
      <Upload onStored={() => setReload((n) => n + 1)} />
      <Filters filter={filter} onChange={apply} />

      <EvalError message={error} />
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        data.runs.length === 0 ? (
          <div className="admin-empty-state">
            <p className="admin-empty-state-title">No stored runs match.</p>
            <p className="admin-hint">
              A run is stored when it runs with <code>--store</code>, or when its run file is
              uploaded above.
            </p>
          </div>
        ) : (
          <>
            <div className="scroll-x">
              <table
                className="admin-table eval-table eval-runs-table"
                data-testid="eval-runs-table"
              >
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Suite</th>
                    <th>Status</th>
                    <th>Model</th>
                    <th>Gate</th>
                    {metrics.map((m) => (
                      <th key={m} title={metricLabel(m)}>
                        {METRIC_HEADS[m]}
                      </th>
                    ))}
                    <th>Judge</th>
                    <th>Cost</th>
                    <th>Code</th>
                  </tr>
                </thead>
                {data.runs.map((run) => (
                  <RunRows key={run.id} run={run} metrics={metrics} />
                ))}
              </table>
            </div>
            {pages !== null && (pages.prev !== null || pages.next !== null) ? (
              <p className="eval-pager">
                <button
                  type="button"
                  className="admin-link-button"
                  disabled={pages.prev === null}
                  onClick={() => apply({ ...filter, offset: pages.prev ?? undefined })}
                >
                  ← Newer
                </button>
                <span className="admin-muted">
                  {pages.from}–{pages.to} of {data.total}
                </span>
                <button
                  type="button"
                  className="admin-link-button"
                  disabled={pages.next === null}
                  onClick={() => apply({ ...filter, offset: pages.next ?? undefined })}
                >
                  Older →
                </button>
              </p>
            ) : null}
            <p className="admin-hint">
              Showing up to {EVAL_PAGE_SIZE} runs a page. Open a run for its cases, its baseline and
              a comparison.
            </p>
          </>
        )
      ) : null}
    </section>
  );
}
